/**
 * API Service
 * Handles all HTTP requests to the backend API with authentication
 */

import * as Sentry from "@sentry/react";

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

/** Normalize endpoint for metrics: strip query params, replace UUIDs with :id */
function normalizeEndpoint(endpoint: string): string {
  return endpoint
    .split('?')[0]
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, ':id');
}

// =============================================================================
// Types
// =============================================================================

export interface Action {
  id: string;
  title: string;
  description: string;
  status: 'open' | 'in_progress' | 'completed' | 'blocked' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'critical';
  category?: string;
  due_date?: string;
  created_at: string;
  updated_at?: string;
  sharepoint_id?: string;
  assigned_by?: string;
}

export interface CreateIssueRequest {
  title: string;
  description: string;
  priority?: 'low' | 'medium' | 'high' | 'critical';
  category?: 'bug' | 'feature' | 'improvement' | 'support' | 'other';
  due_date?: string;
  metadata?: Record<string, unknown>;
}

export interface ApiError {
  error: string;
  details?: Record<string, unknown>;
  retry_after?: number;
}

/** Error with HTTP status and optional Retry-After for 429 responses. */
export class ApiRequestError extends Error {
  status: number;
  retryAfter?: number;

  constructor(message: string, status: number, retryAfter?: number) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.retryAfter = retryAfter;
  }
}

export class AuthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuthError';
  }
}

// =============================================================================
// API Client
// =============================================================================

class ApiClient {
  private baseUrl: string;
  private getToken: (() => Promise<string | null>) | null = null;
  private onUnauthorized: (() => void) | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  setTokenProvider(getToken: () => Promise<string | null>) {
    this.getToken = getToken;
  }

  setOnUnauthorized(handler: () => void) {
    this.onUnauthorized = handler;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const method = (options.method || 'GET').toUpperCase();
    const metricEndpoint = normalizeEndpoint(endpoint);
    const start = performance.now();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    // Add authorization header if token provider is set
    if (this.getToken) {
      const token = await this.getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        console.debug(`[API] Token for ${endpoint}: ${token.substring(0, 20)}...${token.substring(token.length - 10)} (len=${token.length})`);
      } else {
        console.warn(`[API] No auth token for ${endpoint} — request will be unauthenticated`);
      }
    }

    let response: Response;
    try {
      response = await fetch(url, { ...options, headers });
    } catch (err) {
      const duration = performance.now() - start;
      Sentry.metrics.distribution("api.request.duration", duration, {
        unit: "millisecond",
        attributes: { endpoint: metricEndpoint, method, status: "network_error" },
      });
      Sentry.metrics.count("api.error.count", 1, {
        attributes: { endpoint: metricEndpoint, method, error_type: "NetworkError" },
      });
      throw err;
    }

    const duration = performance.now() - start;
    const status = String(response.status);

    Sentry.metrics.distribution("api.request.duration", duration, {
      unit: "millisecond",
      attributes: { endpoint: metricEndpoint, method, status },
    });
    Sentry.metrics.count("api.request.count", 1, {
      attributes: { endpoint: metricEndpoint, method, status },
    });

    if (response.status === 401) {
      const body = await response.clone().text();
      console.error(`[API] 401 Unauthorized for ${endpoint}`, {
        responseBody: body,
        hasAuthHeader: 'Authorization' in headers,
        authHeaderPrefix: headers['Authorization']?.substring(0, 30),
      });
      Sentry.metrics.count("api.error.count", 1, {
        attributes: { endpoint: metricEndpoint, method, error_type: "AuthError" },
      });
      this.onUnauthorized?.();
      throw new AuthError('Unauthorized: server rejected the request.');
    }

    // Handle non-JSON responses
    const contentType = response.headers.get('content-type');
    if (!contentType?.includes('application/json')) {
      if (!response.ok) {
        Sentry.metrics.count("api.error.count", 1, {
          attributes: { endpoint: metricEndpoint, method, error_type: "NonJsonError" },
        });
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return {} as T;
    }

    const data = await response.json();

    if (!response.ok) {
      const error = data as ApiError;
      const message = error.error || `HTTP error! status: ${response.status}`;

      Sentry.metrics.count("api.error.count", 1, {
        attributes: { endpoint: metricEndpoint, method, error_type: `Http${status}` },
      });

      // Preserve retry_after for 429 responses so React Query can honor it
      if (response.status === 429) {
        const retryAfter = error.retry_after
          ?? parseInt(response.headers.get('Retry-After') ?? '20', 10);
        throw new ApiRequestError(message, 429, retryAfter);
      }

      throw new ApiRequestError(message, response.status);
    }

    return data as T;
  }

  // GET request
  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  // POST request
  async post<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  // PUT request
  async put<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  // PATCH request
  async patch<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  // DELETE request
  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

// =============================================================================
// API Instance
// =============================================================================

export const api = new ApiClient(API_BASE_URL);

// =============================================================================
// API Methods
// =============================================================================

export const actionsApi = {
  /**
   * Get user's actions
   */
  getMyActions: async (params?: { status?: string; limit?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.set('status', params.status);
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    
    const query = queryParams.toString();
    const endpoint = query ? `/my-actions?${query}` : '/my-actions';
    
    return api.get<{ actions: Action[]; count: number; user_id: string }>(endpoint);
  },

  /**
   * Create a new issue
   */
  createIssue: async (issue: CreateIssueRequest) => {
    return api.post<Action>('/issues', issue);
  },
};

export const adminApi = {
  /**
   * Trigger manual sync (admin only)
   */
  triggerSync: async (options?: { sync_type?: 'full' | 'incremental'; dry_run?: boolean }) => {
    return api.post<{
      sync_id: string;
      status: string;
      sync_type: string;
      dry_run: boolean;
      stats: Record<string, number>;
    }>('/admin/sync', options);
  },
};

// =============================================================================
// SharePoint Types
// =============================================================================

export interface SharePointFolder {
  id: string;
  name: string;
  path: string;
  child_count: number;
  modified_at: string;
  drive_id?: string;
  site_id?: string;
  parent_id?: string;
}

export interface SharePointFile {
  id: string;
  name: string;
  path: string;
  size: number;
  modified_at: string;
  web_url?: string;
  drive_id?: string;
  site_id?: string;
  parent_id?: string;
}

export interface BreadcrumbItem {
  name: string;
  path: string;
}

export interface FolderStats {
  folder_count: number;
  file_count: number;
  total_size: number;
  excel_count: number;
}

export interface FolderContents {
  folders: SharePointFolder[];
  files: SharePointFile[];
  current_path: string;
  parent_path: string;
  breadcrumbs?: BreadcrumbItem[];
  stats?: FolderStats;
  drive_id?: string;
  site_id?: string;
  /** True when there are more items beyond this page. */
  has_more?: boolean;
  /** Base64-encoded page token for fetching next page. */
  next_page_token?: string;
  /** Total items in the folder (from Graph API childCount, first page only). */
  total_items?: number;
}

export interface FolderScanJob {
  job_id: string;
  status: 'processing' | 'completed' | 'failed';
  folders_scanned: number;
  files_found: number;
  created_at?: string;
  updated_at?: string;
  error_message?: string;
  // When completed, includes FolderContents fields:
  folders?: SharePointFolder[];
  files?: SharePointFile[];
  current_path?: string;
  parent_path?: string;
  breadcrumbs?: BreadcrumbItem[];
  stats?: FolderStats;
}

export interface SyncItem {
  type: 'folder' | 'file';
  id: string;
  path: string;
  drive_id?: string;
  name?: string;
}

export interface SyncResult {
  sync_id: string;
  status: string;
  total: number;
  completed: number;
  failed: number;
  results: {
    completed: Array<{ file: string; path: string; scenario_id?: string }>;
    failed: Array<{ file: string; path: string; error: string }>;
  };
}

// =============================================================================
// SharePoint API
// =============================================================================

export const sharePointApi = {
  /**
   * Get folder contents from SharePoint
   */
  getFolders: async (
    path: string = '/',
    driveId?: string,
    filterExcel: boolean = false,
    recursive: boolean = false,
    pageToken?: string,
  ): Promise<FolderContents> => {
    const params = new URLSearchParams({ path });
    if (driveId) params.set('drive_id', driveId);
    if (filterExcel) params.set('filter_excel', 'true');
    if (recursive) params.set('recursive', 'true');
    if (pageToken) params.set('page_token', pageToken);
    return api.get<FolderContents>(`/sharepoint/folders?${params}`);
  },

  /**
   * Start an async folder scan (returns job_id for polling).
   * Used for recursive Excel file searches or "Select All" on large folders.
   */
  startFolderScan: async (
    path: string,
    driveId?: string,
    recursive: boolean = true,
  ): Promise<FolderScanJob> => {
    const params = new URLSearchParams({ path, filter_excel: 'true', select_all: 'true' });
    if (recursive) params.set('recursive', 'true');
    if (driveId) params.set('drive_id', driveId);
    return api.get<FolderScanJob>(`/sharepoint/folders?${params}`);
  },

  /**
   * Poll for async folder scan job status.
   */
  getFolderScanStatus: async (jobId: string): Promise<FolderScanJob> => {
    return api.get<FolderScanJob>(`/sharepoint/folders?job_id=${encodeURIComponent(jobId)}`);
  },

  /**
   * Sync selected items from SharePoint
   */
  syncItems: async (
    items: SyncItem[],
    options: { recursive?: boolean } = {}
  ): Promise<SyncResult> => {
    return api.post<SyncResult>('/sharepoint/sync', { items, options });
  },
};

